import { useState } from 'react';
import { DialogActions, DialogContent, FormGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { ConsentResponse, UpdateConsentsRequest } from '@/models/company';
import {
    ConsentStyledDialog,
    StyledDialogTitle,
    StyledDynamicContentBox
} from '@/components/consent/StyledDialog';
import { ConsentCheckbox } from '@/components/consent/ConsentCheckbox';
import {
    PrimaryButton,
    PrimaryInvertedButton
} from '@/components/shared/Button';
import { SemiBold, SMRegular, TextMDSemiBold } from '@/typography/Typography';
import { useUsersMutations } from '@/services/usersMutations';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';

interface AcceptConsentDialogProps {
    consent: ConsentResponse;
    allConsents: ConsentResponse[];
    onClose: () => void;
}

export const AcceptConsentDialog = ({
    consent,
    allConsents,
    onClose
}: AcceptConsentDialogProps) => {
    const { t } = useTranslation();
    const [checked, setChecked] = useState(false);
    const { updateConsents } = useUsersMutations();

    useHandleResponse({ error: updateConsents.error ?? null });

    const isData = consent.consentType === 'Data';
    const isCommercial = consent.consentType === 'Commercial';

    const getTitle = () => {
        if (isData) return t('data_processing_agreement');
        if (isCommercial) return t('license_agreement');
        return t('marketing_updates');
    };

    const getCheckboxLabel = () => {
        if (isData) {
            return (
                <>
                    <span>{t('i_have_read_and_accepted_the')} </span>
                    {consent.termsVersionUrl ? (
                        <a
                            href={consent.termsVersionUrl}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {t('data_processing_agreement').toLowerCase()}
                        </a>
                    ) : (
                        <span>
                            {t('data_processing_agreement').toLowerCase()}
                        </span>
                    )}
                    .
                </>
            );
        }
        if (isCommercial) {
            return (
                <>
                    <span>{t('i_have_read_and_accepted_the')} </span>
                    {consent.termsVersionUrl ? (
                        <a
                            href={consent.termsVersionUrl}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {t('license_agreement').toLowerCase()}
                        </a>
                    ) : (
                        <span>{t('license_agreement').toLowerCase()}</span>
                    )}
                    .
                </>
            );
        }
        return t('newsletter_consent_label');
    };

    const handleAccept = () => {
        if (!checked) return;

        const request: UpdateConsentsRequest = isCommercial
            ? {
                  commercialConsented: isCommercial,
                  commercialTermsVersion: consent.termsVersionId
              }
            : {
                  dataConsented: isData,
                  dataTermsVersion: consent.termsVersionId
              };

        updateConsents.mutate(request, {
            onSuccess: () => {
                onClose();
                window.location.reload();
            }
        });
    };

    return (
        <ConsentStyledDialog
            open={true}
            fullWidth
            maxWidth="sm"
            onClose={onClose}
            slotProps={{
                paper: {
                    style: {
                        borderRadius: '12px',
                        boxShadow:
                            '0px 8px 8px -4px #10182808, 0px 20px 24px -4px #10182814',
                        width: '664px'
                    }
                }
            }}
        >
            <StyledDialogTitle style={{ padding: '16px' }}>
                <img
                    style={{ marginBottom: '16px' }}
                    src="/consent_top_logo.svg"
                    alt="InstaPaid Logo"
                    width={150}
                    height={50}
                />
                <SemiBold>{getTitle()}</SemiBold>
                {consent.termsVersionName && (
                    <SMRegular style={{ marginTop: '8px', color: '#667085' }}>
                        {consent.termsVersionUrl ? (
                            <a
                                href={consent.termsVersionUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {consent.termsVersionName}
                            </a>
                        ) : (
                            consent.termsVersionName
                        )}
                    </SMRegular>
                )}
            </StyledDialogTitle>

            <StyledDynamicContentBox>
                <SMRegular>
                    <p>
                        {t('please_review_and_accept_the_document_to_continue')}
                    </p>
                </SMRegular>
            </StyledDynamicContentBox>

            <DialogContent sx={{ color: 'black', padding: '20px 0px' }}>
                <FormGroup>
                    <ConsentCheckbox
                        checked={checked}
                        setChecked={setChecked}
                        label={getCheckboxLabel()}
                    />
                </FormGroup>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'center', gap: 1 }}>
                <PrimaryInvertedButton
                    fullWidth
                    size="large"
                    variant="outlined"
                    style={{ height: '44px' }}
                    onClick={onClose}
                >
                    {t('cancel')}
                </PrimaryInvertedButton>
                <PrimaryButton
                    fullWidth
                    size="large"
                    style={{ height: '44px' }}
                    disabled={!checked || updateConsents.isPending}
                    onClick={handleAccept}
                >
                    {t('accept')}
                </PrimaryButton>
            </DialogActions>
        </ConsentStyledDialog>
    );
};
